import { Client, Callbacks, type Room } from '@colyseus/sdk';
import {
  ClientMessage,
  ServerMessage,
  VoiceClientMessage,
  VoiceServerMessage,
  type ActiveSpeakersNotice,
  type CelebrationBroadcast,
  type DuckBurstBroadcast,
  type CelebrationKind,
  type DuckColor,
  type DuckSpecies,
  type EquippedCosmetics,
  type NewProducerNotice,
  type PresenceFlavor,
  type ProducerClosedNotice,
  type Reaction,
  type ReactedBroadcast,
  type MediaControlMessage,
  type MediaReactedBroadcast,
  type MediaClock,
  type MediaHubStateView,
  type MediaItemView,
  type MediaSourceKind,
  type FurnitureResult,
  type PlacedFurnitureView,
  type ReputationResult,
  type RpsChoice,
  type RpsInvite,
  type RpsResultMessage,
  type RpsStarted,
  type BarkInvite,
  type BarkPending,
  type BarkStart,
  type BarkTick,
  type BarkResultMessage,
  type SaidBroadcast,
  type SoundReactedBroadcast,
  type SoundReactionId,
  type BellRangBroadcast,
  type KnockedBroadcast,
  type VoiceRpcRequest,
  type VoiceRpcResult,
  type OfficeQuestProgressView,
  type QuestFired,
  type Weather,
  type WorldAmbient,
  type WorldEventKind,
  type WorldThemeId,
  type LevelId,
} from '@duck/contracts';

/** Map one raw Colyseus MediaItem schema node (ADR-0033) to a plain {@link MediaItemView}, defensively. */
function mapMediaItem(raw: Record<string, unknown> | undefined): MediaItemView | null {
  if (!raw || typeof raw !== 'object') return null;
  const id = typeof raw.id === 'string' ? raw.id : '';
  if (!id) return null; // an empty `now` sub-schema (idle) reads as null
  return {
    id,
    kind: (typeof raw.kind === 'string' ? raw.kind : 'youtube') as MediaSourceKind,
    ref: typeof raw.ref === 'string' ? raw.ref : '',
    title: typeof raw.title === 'string' ? raw.title : '',
    addedByUserId: typeof raw.addedByUserId === 'string' ? raw.addedByUserId : '',
    addedByName: typeof raw.addedByName === 'string' ? raw.addedByName : '',
  };
}

/** Drain a Colyseus ArraySchema<MediaItem> (array-LIKE, not a real Array) into a plain MediaItemView[]. */
function mapMediaItems(raw: unknown): MediaItemView[] {
  const out: MediaItemView[] = [];
  const arr = raw as { forEach?: (cb: (v: unknown) => void) => void } | undefined;
  if (arr && typeof arr.forEach === 'function') {
    arr.forEach((v) => {
      const item = mapMediaItem(v as Record<string, unknown>);
      if (item) out.push(item);
    });
  }
  return out;
}

/** Map one raw MediaHubState schema node (ADR-0033) to a plain {@link MediaHubStateView}, defensively. */
function mapMediaHub(raw: Record<string, unknown>, zoneId: string): MediaHubStateView {
  return {
    zoneId: typeof raw.zoneId === 'string' ? raw.zoneId : zoneId,
    hostSessionId: typeof raw.hostSessionId === 'string' ? raw.hostSessionId : '',
    hostName: typeof raw.hostName === 'string' ? raw.hostName : '',
    now: mapMediaItem(raw.now as Record<string, unknown>),
    playing: raw.playing === true,
    positionMs: typeof raw.positionMs === 'number' ? raw.positionMs : 0,
    updatedAt: typeof raw.updatedAt === 'number' ? raw.updatedAt : 0,
    epoch: typeof raw.epoch === 'number' ? raw.epoch : 0,
    queue: mapMediaItems(raw.queue),
    history: mapMediaItems(raw.history),
  };
}

/**
 * Plain-object view of the authoritative player schema synced by the server. These references are
 * mutated in-place by the Colyseus SDK as patches arrive, so the render loop can read them every
 * frame and sample positions for interpolation (see ADR-0004). We deliberately keep this hot data
 * OUT of React/Zustand to avoid 20 Hz re-renders.
 */
export interface SchemaPlayer {
  userId: string;
  displayName: string;
  duckColor: string;
  /** The citizen's chosen ANIMAL (ADR-0024) — synced like duckColor; '' / unknown renders the duck. */
  species: string;
  x: number;
  y: number;
  facing: number;
  activity: string;
  animation: string;
  /** ELECTED presence flair the citizen chose to display (never inferred). 'none' = no badge (M4). */
  presence: string;
  /** Current health (ADR-0052) — drops while standing in fire; 0 → piñata burst + full-HP respawn. */
  hp: number;
  /** True while standing in an open flame (ADR-0052) — drives the on-duck "burning" VFX. */
  burning: boolean;
  /**
   * EQUIPPED cosmetics (M6 "Express Yourself") — the citizen's chosen LOOK, synced to everyone.
   * Mutated in-place by the Colyseus SDK like the other hot fields, so the render loop reads it every
   * frame. The wire shape is a nested sub-schema with the four slot fields where '' means an EMPTY slot
   * (no cosmetic) — the renderer treats '' the same as absent. Cosmetic-only; ownership/coins are
   * gateway-attested, the realtime only carries the display.
   */
  cosmetics: { hat: string; glasses: string; outfit: string; pet: string };
  /**
   * The SEAT this duck is sitting on (Task 2 "interactive SIT"), or '' when standing. While set the
   * server snaps the duck to the seat anchor + ignores its move intent; the duck plays the 'sit' pose.
   * Mutated in place by the Colyseus SDK like the other hot fields.
   */
  seatedAt: string;
  /** The fun prop held in hand (Task 2 "HOLD ITEMS") — a `HELD_ITEMS` id, or '' for empty hands. */
  heldItem: string;
  lastSeq: number;
  /** Audio-device presence for the participant list (Discord-style 🎤/🎧). `voiceOn` = voice is live so
   *  this citizen can HEAR the room; `micMuted` = their mic is deliberately muted. Default off until they
   *  enable voice. Set via {@link RealtimeClient.sendVoiceState}, broadcast to all by the server. */
  voiceOn: boolean;
  micMuted: boolean;
}

/**
 * Connection health, surfaced to React so the HUD can show a gentle reconnection state (M3.5b).
 *  - `connected`    — the room socket is live (the normal, invisible-to-the-player state).
 *  - `reconnecting` — the socket dropped UNEXPECTEDLY (a blip / server restart) and we're transparently
 *    re-claiming the SAME seat within the server's grace window. The world stays on screen.
 *  - `lost`         — the grace window elapsed (all retries failed); the seat is gone, a calm reload
 *    is the way back. NEVER reached by a deliberate leave (logout / navigating away).
 */
export type ConnectionStatus = 'connected' | 'reconnecting' | 'lost';

export interface ConnectParams {
  url: string;
  accessToken: string;
  workspaceId: string;
  duckColor: DuckColor;
  /** The citizen's chosen ANIMAL (ADR-0024) — passed on join so the room broadcasts it to everyone. */
  species: DuckSpecies;
  /**
   * The guild's chosen world THEME (M8 "Guilds at Scale") — the SAME engine, re-skinned per guild. The
   * client passes its workspace's theme on join so the room skins the world for everyone; the server
   * also broadcasts it on the room state (read via {@link theme}) so a later owner change re-skins live.
   * Cosmetic + guild-wide; defaults to the office (the realtime applies the same default).
   */
  theme: WorldThemeId;
  /** Called when a player joins/leaves so the React HUD can refresh its roster. */
  onRosterChange?: () => void;
  /** Called for every celebration broadcast so the HUD can refresh stats (the "moment of joy"). */
  onCelebration?: (celebration: CelebrationBroadcast) => void;
  /**
   * Someone said something (M7a "Socialize"). Fired for SELF and OTHERS so the React layer can mirror
   * the line into the accessible "recent chatter" strip (Pixi bubbles are invisible to assistive tech).
   * The Pixi bubble itself is spawned by the render loop draining {@link pendingSaids}.
   */
  onSaid?: (said: SaidBroadcast) => void;
  /**
   * Someone sent an emoji reaction (M7a). Fired for SELF and OTHERS so the React layer can mirror it
   * into the accessible chatter strip. The floating emoji visual is spawned by the render loop draining
   * {@link pendingReacts}.
   */
  onReacted?: (reacted: ReactedBroadcast) => void;
  /**
   * Someone played a SOUND reaction (Task 6b "Sound reactions"). Fired for SELF and OTHERS (the server
   * echoes ours back) so the React layer can play the short, shared sound for everyone — the sonic cousin
   * of {@link onReacted}. Carries only the closed-set `sound` id (the client plays `/sounds/<id>.mp3`).
   */
  onSoundReacted?: (reacted: SoundReactedBroadcast) => void;
  /**
   * Someone reacted on a zone's MEDIA HUB screen (ADR-0033). Fired for SELF and OTHERS (the server echoes
   * ours back, zone-scoped) so the React layer can float the emoji on the shared screen + mirror it into
   * the accessible chatter strip. Carries the closed-set `reaction`, the sender's session, and the zoneId.
   */
  onMediaReacted?: (reacted: MediaReactedBroadcast) => void;
  /**
   * Someone rang the entry BELL (ADR-0022) — sent to EVERYONE, so the React layer plays the doorbell
   * chime + a gentle "🔔 Someone's at the door!" cue for the whole office. Carries the ringer's name.
   */
  onBellRang?: (bell: BellRangBroadcast) => void;
  /**
   * Someone KNOCKED on the entry door (ADR-0022) — sent ONLY to the nearby sessions the server picked,
   * so just ducks near the door hear the soft knock. The React layer plays the knock for the listener.
   */
  onKnocked?: (knock: KnockedBroadcast) => void;
  /** Private reply to one of OUR give-reputation attempts (success + remaining budget, or why not). */
  onReputationResult?: (result: ReputationResult) => void;
  /** Private reply to one of OUR place-furniture attempts (success + new coin balance, or why not —
   * "Not enough coins", "This room is full"). The placed piece itself arrives via the synced state. */
  onFurnitureResult?: (result: FurnitureResult) => void;
  /**
   * A cooperative OFFICE QUEST I took part in just fired (gamified-XP redesign). Sent PRIVATELY to each
   * participant, so the HUD can toast "✨ {quest} · +{xp} XP" and refetch progression so the rising XP
   * is visible. The matching guild-wide CELEBRATE 'quest_completed' broadcast handles the world-reacts-
   * together confetti for the headline raids. The React layer is expected to throttle/coalesce so the
   * tiny WORK +1 ticks + the per-minute participation grants never spam.
   */
  onQuestFired?: (quest: QuestFired) => void;
  /**
   * Someone challenged ME to Rock-Paper-Scissors (M7b "Play Together", emotion: Tradition). The server
   * proximity-checks the challenger and creates the match, then sends this only to the challenged duck —
   * so the React layer can pop a friendly invite prompt with the three choice buttons + Decline.
   */
  onRpsInvite?: (invite: RpsInvite) => void;
  /**
   * MY challenge created a match (M7b "Play Together"). The server proximity-checks my challenge, mints
   * the match, sends RPS_INVITE to the target, and sends THIS (RPS_STARTED) back to me — the CHALLENGER —
   * carrying the server-minted matchId + my opponent, so the React layer can immediately show ME the
   * three choice buttons (the in-match 'choosing' card). Distinct from {@link onRpsInvite}, which fires
   * on the OTHER side (the duck I challenged).
   */
  onRpsStarted?: (started: RpsStarted) => void;
  /**
   * A match I'm in resolved or ended (M7b). Sent to BOTH players. `outcome` is from MY point of view
   * (win = I won); choices are present only when both actually played. The React layer shows the result
   * warmly (a small celebration on a win) and refetches progression so the PLAY_MINIGAME coin/XP grant
   * lands on the HUD. `declined`/`expired` are handled gracefully (no grant).
   */
  onRpsResult?: (result: RpsResultMessage) => void;
  /**
   * Someone challenged ME to a BARK BATTLE (Challenge game #2 — contracts domain/bark.ts). Sent only
   * to the challenged duck so the React layer can pop the accept/decline prompt ("bark louder than
   * them for 10 seconds!"). Accepting needs a live, unmuted mic — the prompt nudges for it.
   */
  onBarkInvite?: (invite: BarkInvite) => void;
  /** MY bark challenge is out (the CHALLENGER's mirror of {@link onBarkInvite}): the server-minted
   * matchId + opponent, so the React layer shows a "waiting on them…" card. */
  onBarkPending?: (pending: BarkPending) => void;
  /** Both ducks on accept: run the local 3-2-1 (`countdownMs`), then sample the mic for `durationMs`. */
  onBarkStart?: (start: BarkStart) => void;
  /** ~10×/s during the window: the server's authoritative running totals, from MY point of view. */
  onBarkTick?: (tick: BarkTick) => void;
  /** A battle I'm in ended (my POV; scores present only when the window was actually played). */
  onBarkResult?: (result: BarkResultMessage) => void;
  /**
   * Connection health changed (M3.5b). Drives the gentle "Reconnecting…" HUD indicator. Fired on an
   * UNEXPECTED drop (→ `reconnecting`), on a successful re-claim of the same seat (→ `connected`), and
   * when the grace window elapses (→ `lost`). A deliberate leave never fires `reconnecting`/`lost`.
   */
  onConnectionStatus?: (status: ConnectionStatus) => void;
  /**
   * The room socket was re-established into the SAME seat after an unexpected drop (M3.5b). Colyseus
   * resyncs the room state + keeps our message handlers, so the world is already coherent here — this
   * is the hook to RE-RUN the voice handshake (the SFU tore our media down on disconnect), which the
   * VoiceClient registers. Distinct from `onConnectionStatus('connected')`, which also fires on the
   * very first connect; this fires ONLY after a real reconnect.
   */
  onReconnected?: () => void;
}

/** How long (ms) we wait for a `voice:result` before rejecting a voice RPC (handshake stalled). */
const VOICE_RPC_TIMEOUT_MS = 10_000;

/**
 * Distributive Omit: applies `Omit` to each member of a union separately, so a discriminated union
 * stays a discriminated union (and keeps exact-property checks per member) after dropping a field.
 */
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

/** A voice RPC request as callers supply it — the `id` is generated by {@link RealtimeClient}. */
export type VoiceRpcRequestInput = DistributiveOmit<VoiceRpcRequest, 'id'>;

/** A single in-flight voice RPC, keyed by its request `id`, awaiting its matching `voice:result`. */
interface PendingVoiceRpc {
  resolve: (data: unknown) => void;
  reject: (err: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

/**
 * Hard cap on each transient FX queue (celebrations / speech / reactions). These are drained EVERY
 * frame by the Pixi loop, so they sit near-empty in steady state — the cap only bites when the loop is
 * stalled (a backgrounded tab the browser has throttled) in a chatty room. We keep the most RECENT
 * events and drop the oldest, so returning to the tab shows the latest moments without unbounded growth.
 */
const MAX_PENDING_FX = 64;

/** Push onto a transient FX queue, dropping the oldest entries so it can never grow without bound. */
function pushBoundedFx<T>(queue: T[], item: T): void {
  queue.push(item);
  if (queue.length > MAX_PENDING_FX) queue.splice(0, queue.length - MAX_PENDING_FX);
}

/**
 * Typed wrapper around a Colyseus room. The rest of the app depends on this interface, not on the
 * Colyseus SDK — which keeps the door open to swap transports later (ADR-0003).
 */
export class RealtimeClient {
  readonly players = new Map<string, SchemaPlayer>();
  /** Celebrations received since the last drain — the render loop pops these to spawn confetti. */
  readonly pendingCelebrations: CelebrationBroadcast[] = [];
  /** Duck "piñata bursts" received since the last drain (ADR-0052) — render loop pops these to splatter. */
  readonly pendingBursts: DuckBurstBroadcast[] = [];
  /**
   * Speech lines received since the last drain (M7a) — the render loop pops these to pop a bubble over
   * the speaker's duck. Kept OUT of React (like celebrations) so chatter never triggers a re-render.
   */
  readonly pendingSaids: SaidBroadcast[] = [];
  /** Emoji reactions received since the last drain (M7a) — the render loop pops these to float an emoji. */
  readonly pendingReacts: ReactedBroadcast[] = [];
  /** Most recent speech line received (M7a) — exposed via the dev `__duckSocial()` hook for the e2e. */
  lastSaid: SaidBroadcast | null = null;
  /** Most recent reaction received (M7a) — exposed via the dev `__duckSocial()` hook for the e2e. */
  lastReacted: ReactedBroadcast | null = null;
  /** Most recent SOUND reaction received (Task 6b) — exposed via the dev `__duckSound()` hook for the e2e. */
  lastSoundReacted: SoundReactedBroadcast | null = null;
  /** Most recent media-hub screen reaction received (ADR-0033) — exposed via the dev `__duckMedia()` hook. */
  lastMediaReacted: MediaReactedBroadcast | null = null;
  /**
   * Estimated client→server clock offset for the MEDIA HUB (ADR-0033): `serverDateNow ≈ Date.now() + this`.
   * Derived from the MEDIA_CLOCK reply to a `sync` probe (RTT/2 midpoint). Lets the player extrapolate the
   * shared playback position even if the local wall-clock is skewed. 0 until the first probe resolves.
   */
  mediaClockOffsetMs = 0;
  /** Most recent entry BELL ring received (ADR-0022) — exposed via the dev `__duckEntry()` hook for the e2e. */
  lastBellRang: BellRangBroadcast | null = null;
  /** Most recent entry KNOCK received (ADR-0022) — exposed via the dev `__duckEntry()` hook for the e2e. */
  lastKnocked: KnockedBroadcast | null = null;
  /** Most recent RPS invite received (M7b) — exposed via the dev `__duckRps()` hook for the e2e. */
  lastRpsInvite: RpsInvite | null = null;
  /** Most recent RPS_STARTED (my challenge created a match, M7b) — for the dev `__duckRps()` hook. */
  lastRpsStarted: RpsStarted | null = null;
  /** Most recent RPS result received (M7b) — exposed via the dev `__duckRps()` hook for the e2e. */
  lastRpsResult: RpsResultMessage | null = null;
  /** Most recent Bark Battle one-shots — exposed via the dev `__duckBark()` hook for the e2e. */
  lastBarkInvite: BarkInvite | null = null;
  lastBarkPending: BarkPending | null = null;
  lastBarkStart: BarkStart | null = null;
  lastBarkTick: BarkTick | null = null;
  lastBarkResult: BarkResultMessage | null = null;
  /** Most recent OFFICE-QUEST grant received (gamified-XP) — exposed via the dev `__duckQuests()` hook. */
  lastQuestFired: QuestFired | null = null;
  selfSessionId = '';
  rttMs = 0;

  // ── Reconnection grace (M3.5b). ──
  /**
   * Live connection health. The Colyseus SDK (v0.17) reconnects the SAME Room instance internally on an
   * abnormal close — reusing `room.reconnectionToken`, resyncing state, and KEEPING our message + schema
   * handlers — so we ride its signals (`onDrop`/`onReconnect`/`onLeave`) rather than re-implementing the
   * retry loop (which would double-connect the same seat). See {@link configureReconnection}.
   */
  private status: ConnectionStatus = 'connected';
  /** How many times we've transparently re-claimed our seat this session (drives the e2e + HUD). */
  private reconnectCount = 0;
  /**
   * Set the instant WE initiate a leave (logout / navigating away / unmount). A consented leave already
   * routes through Colyseus' terminal `onLeave` (not `onDrop`), so it never reconnects — but this guard
   * makes the intent explicit so a terminal `onLeave` is reported as a clean exit, never as `lost`.
   */
  private intentionalLeave = false;
  private onConnectionStatusCb: ((status: ConnectionStatus) => void) | null = null;
  private onReconnectedCb: (() => void) | null = null;

  // ── Voice signaling (rides the room socket — ADR-0009). ──
  /** In-flight voice RPCs awaiting their matching `voice:result`, keyed by request id. */
  private readonly pendingVoiceRpc = new Map<string, PendingVoiceRpc>();
  /** Monotonic counter feeding unique voice RPC ids. */
  private voiceRpcSeq = 0;
  /** Subscriber callbacks for server-driven voice notifications (the voice client registers these). */
  private onNewProducerCb: ((notice: NewProducerNotice) => void) | null = null;
  private onProducerClosedCb: ((notice: ProducerClosedNotice) => void) | null = null;
  private onActiveSpeakersCb: ((notice: ActiveSpeakersNotice) => void) | null = null;

  private constructor(
    private readonly room: Room,
    private pingTimer: ReturnType<typeof setInterval> | null,
  ) {
    this.selfSessionId = room.sessionId;
  }

  static async connect(params: ConnectParams): Promise<RealtimeClient> {
    const client = new Client(params.url);
    const room = await client.joinOrCreate('office', {
      accessToken: params.accessToken,
      workspaceId: params.workspaceId,
      duckColor: params.duckColor,
      species: params.species, // ADR-0024: the citizen's chosen animal — broadcast to everyone.
      theme: params.theme, // M8: the guild's world theme — the room skins the world for everyone.
    });

    const instance = new RealtimeClient(room, null);
    instance.onConnectionStatusCb = params.onConnectionStatus ?? null;
    instance.onReconnectedCb = params.onReconnected ?? null;
    instance.configureReconnection();
    const callbacks = Callbacks.get(room);

    callbacks.onAdd('players', (player: unknown, sessionId: unknown) => {
      instance.players.set(String(sessionId), player as SchemaPlayer);
      params.onRosterChange?.();
    });
    callbacks.onRemove('players', (_player: unknown, sessionId: unknown) => {
      instance.players.delete(String(sessionId));
      params.onRosterChange?.();
    });

    room.onMessage(ServerMessage.PONG, (msg: { t: number }) => {
      instance.rttMs = Math.round(performance.now() - msg.t);
    });

    room.onMessage(ServerMessage.CELEBRATE, (msg: CelebrationBroadcast) => {
      pushBoundedFx(instance.pendingCelebrations, msg); // drained by the render loop → confetti
      params.onCelebration?.(msg); // let React refresh stats so the numbers move with the joy
    });

    room.onMessage(ServerMessage.DUCK_BURST, (msg: DuckBurstBroadcast) => {
      pushBoundedFx(instance.pendingBursts, msg); // drained by the render loop → piñata splatter (ADR-0052)
    });

    // M7a "Socialize": speech bubbles + emoji reactions. The render loop drains the pending queues to
    // pop a bubble / float an emoji over the speaker's duck (SELF and OTHERS — the server echoes ours
    // back); React mirrors the same event into the accessible chatter strip (Pixi is invisible to AT).
    room.onMessage(ServerMessage.SAID, (msg: SaidBroadcast) => {
      pushBoundedFx(instance.pendingSaids, msg); // drained by the render loop → speech bubble
      instance.lastSaid = msg; // for the dev __duckSocial() hook
      params.onSaid?.(msg); // → accessible "recent chatter" strip
    });
    room.onMessage(ServerMessage.REACTED, (msg: ReactedBroadcast) => {
      pushBoundedFx(instance.pendingReacts, msg); // drained by the render loop → floating emoji
      instance.lastReacted = msg; // for the dev __duckSocial() hook
      params.onReacted?.(msg); // → accessible "recent chatter" strip
    });
    // Task 6b "Sound reactions": someone played a short, shared sound (SELF and OTHERS — the server
    // echoes ours back). The server PROXIMITY-GATES this like voice, so we only ever receive it when the
    // sender is audible to us (same zone / within hearing range); the sender is always sent their own.
    // No render-loop visual is required (audio-only); the React layer plays the clip via `onSoundReacted`.
    // We stash the last for the dev `__duckSound()` hook.
    room.onMessage(ServerMessage.SOUND_REACTED, (msg: SoundReactedBroadcast) => {
      instance.lastSoundReacted = msg; // for the dev __duckSound() hook
      params.onSoundReacted?.(msg); // → play the clip (server already gated to audible listeners)
    });

    // MEDIA HUB (ADR-0033). The per-zone playback STATE syncs via the schema (read with {@link media});
    // these two messages are the transient extras: a private clock-probe reply (to derive the offset for
    // position extrapolation) and a zone-scoped screen reaction (SELF + OTHERS — the server echoes ours).
    room.onMessage(ServerMessage.MEDIA_CLOCK, (msg: MediaClock) => {
      // RTT/2 midpoint estimate: serverDateNow ≈ Date.now() + offset. Keep it in the Date.now() epoch so
      // it composes directly with the server-stamped `updatedAt` on the hub state.
      const rtt = Date.now() - msg.t;
      instance.mediaClockOffsetMs = msg.serverNow + rtt / 2 - Date.now();
    });
    room.onMessage(ServerMessage.MEDIA_REACTED, (msg: MediaReactedBroadcast) => {
      instance.lastMediaReacted = msg; // for the dev __duckMedia() hook
      params.onMediaReacted?.(msg); // → float the emoji on the zone's screen + chatter strip
    });

    // Entryway front door (ADR-0022): the bell chime (everyone) + the door knock (nearby only). Both are
    // one-shot audio cues; the React layer synthesizes + plays them. We stash the last of each for the dev
    // `__duckEntry()` hook so the e2e can assert a ring/knock landed even though the sound is AT-invisible.
    room.onMessage(ServerMessage.BELL_RANG, (msg: BellRangBroadcast) => {
      instance.lastBellRang = msg;
      params.onBellRang?.(msg);
    });
    room.onMessage(ServerMessage.KNOCKED, (msg: KnockedBroadcast) => {
      instance.lastKnocked = msg;
      params.onKnocked?.(msg);
    });

    room.onMessage(ServerMessage.FURNITURE_RESULT, (msg: FurnitureResult) => {
      params.onFurnitureResult?.(msg);
    });
    room.onMessage(ServerMessage.REPUTATION_RESULT, (msg: ReputationResult) => {
      params.onReputationResult?.(msg);
    });

    // Gamified-XP redesign: a co-op office quest I took part in just fired (a meeting held, a coffee
    // break shared, a desk-work tick…). Sent PRIVATELY per participant. We stash the last for the dev
    // `__duckQuests()` hook and fan it to React, which toasts "✨ {name} · +{xp} XP" (throttled) and
    // refetches progression so the rising XP is visible. The world-reacts-together confetti for the
    // headline raids rides the separate CELEBRATE 'quest_completed' broadcast.
    room.onMessage(ServerMessage.QUEST_FIRED, (msg: QuestFired) => {
      instance.lastQuestFired = msg; // for the dev __duckQuests() hook
      params.onQuestFired?.(msg); // → warm toast + progression refetch
    });

    // M7b "Play Together" (emotion: Tradition): Rock-Paper-Scissors. The server owns proximity + the
    // outcome (server-authoritative); these are one-shot events that drive the React invite prompt /
    // result. We stash the last of each for the dev `__duckRps()` hook so the e2e can drive a match.
    room.onMessage(ServerMessage.RPS_INVITE, (msg: RpsInvite) => {
      instance.lastRpsInvite = msg; // for the dev __duckRps() hook
      params.onRpsInvite?.(msg); // → friendly invite prompt with the three choices + Decline
    });
    room.onMessage(ServerMessage.RPS_STARTED, (msg: RpsStarted) => {
      instance.lastRpsStarted = msg; // for the dev __duckRps() hook
      params.onRpsStarted?.(msg); // → CHALLENGER's choose surface (the in-match 'choosing' card)
    });
    room.onMessage(ServerMessage.RPS_RESULT, (msg: RpsResultMessage) => {
      instance.lastRpsResult = msg; // for the dev __duckRps() hook
      params.onRpsResult?.(msg); // → warm result + refetch progression (PLAY_MINIGAME grant)
    });

    // Bark Battle (Challenge game #2): invite/pending → prompt surfaces, start → local countdown +
    // mic sampler, tick → the authoritative score bar, result → warm ending + progression refetch.
    // The last of each is stashed for the dev `__duckBark()` hook so the e2e can drive a battle.
    room.onMessage(ServerMessage.BARK_INVITE, (msg: BarkInvite) => {
      instance.lastBarkInvite = msg;
      params.onBarkInvite?.(msg);
    });
    room.onMessage(ServerMessage.BARK_PENDING, (msg: BarkPending) => {
      instance.lastBarkPending = msg;
      params.onBarkPending?.(msg);
    });
    room.onMessage(ServerMessage.BARK_START, (msg: BarkStart) => {
      instance.lastBarkStart = msg;
      params.onBarkStart?.(msg);
    });
    room.onMessage(ServerMessage.BARK_TICK, (msg: BarkTick) => {
      instance.lastBarkTick = msg;
      params.onBarkTick?.(msg);
    });
    room.onMessage(ServerMessage.BARK_RESULT, (msg: BarkResultMessage) => {
      instance.lastBarkResult = msg;
      params.onBarkResult?.(msg);
    });

    // ── Voice signaling (ADR-0009): the SERVER decides who is audible. ──
    // RPC replies resolve the matching promise; notifications fan out to the voice client.
    room.onMessage(VoiceServerMessage.RPC_RESULT, (msg: VoiceRpcResult) => {
      instance.settleVoiceRpc(msg);
    });
    room.onMessage(VoiceServerMessage.NEW_PRODUCER, (msg: NewProducerNotice) => {
      instance.onNewProducerCb?.(msg);
    });
    room.onMessage(VoiceServerMessage.PRODUCER_CLOSED, (msg: ProducerClosedNotice) => {
      instance.onProducerClosedCb?.(msg);
    });
    room.onMessage(VoiceServerMessage.ACTIVE_SPEAKERS, (msg: ActiveSpeakersNotice) => {
      instance.onActiveSpeakersCb?.(msg);
    });

    // Latency probe every 2s; piggyback a MEDIA HUB clock-sync probe ~every 10s (ADR-0033) so the player's
    // position extrapolation stays honest even if the local wall-clock drifts. The first tick probes
    // immediately so the offset is fresh before any item plays. Both are tiny echoes.
    let pingTick = 0;
    instance.pingTimer = setInterval(() => {
      room.send(ClientMessage.PING, { t: performance.now() });
      if (pingTick % 5 === 0) room.send(ClientMessage.MEDIA, { action: 'sync', t: Date.now() });
      pingTick++;
    }, 2000);

    return instance;
  }

  // ── Reconnection grace (M3.5b) ──────────────────────────────────────────────────────────────────

  /**
   * Wire the Colyseus SDK's built-in reconnection to our connection-health surface, and tune its window
   * to the server's ~20s grace.
   *
   * Why ride the SDK instead of calling `client.reconnect(token)` ourselves: in v0.17 the Room reconnects
   * IN PLACE on an abnormal close — it re-opens the SAME socket with the latest `reconnectionToken`,
   * resyncs the schema, and KEEPS the registered message + `onAdd`/`onRemove` handlers (they're only
   * cleared on a terminal `onLeave`). A parallel manual `client.reconnect()` would create a SECOND room
   * for the same seat and fight the SDK's retry loop. So we hook its signals and shape its backoff:
   *   • `onDrop`      — an unexpected close started the grace retries → status `reconnecting`.
   *   • `onReconnect` — the SAME seat was re-claimed → status `connected`, re-fix our sessionId, bump the
   *                     reconnect counter, and fire `onReconnected` so voice re-handshakes.
   *   • `onLeave`     — terminal. A deliberate leave (we set {@link intentionalLeave}) is a clean exit;
   *                     anything else here means the grace window elapsed → status `lost`.
   *
   * Backoff sizing: retries at ~100ms · 200ms · 400ms · 800ms · 1.6s · 3.2s · 5s (capped) ≈ 4 attempts
   * land inside the first ~1.5s and the rest spread across the ~20s window, so a brief blip recovers
   * almost instantly while a longer outage keeps trying until the seat is actually gone.
   */
  private configureReconnection(): void {
    const r = this.room.reconnection;
    r.enabled = true;
    r.maxRetries = 8; // ~100ms→5s backoff fills the server's ~20s grace, then we surface `lost`.
    r.minDelay = 100;
    r.maxDelay = 5000;
    // Re-claim even a very-fresh seat (the default 5s min-uptime would surface `lost` for an early blip).
    r.minUptime = 0;

    this.room.onDrop(() => {
      // The socket closed abnormally; the SDK is now retrying. Reassure, don't alarm.
      this.setStatus('reconnecting');
    });

    this.room.onReconnect(() => {
      // Same seat, same Room — state is resynced and handlers are intact. sessionId is preserved on a
      // reconnect, but re-read it defensively so `self()`/voice keep keying off the right id.
      this.selfSessionId = this.room.sessionId;
      this.reconnectCount += 1;
      this.setStatus('connected');
      // Re-probe the media clock (ADR-0033): a fresh offset after the gap keeps playback extrapolation honest.
      this.room.send(ClientMessage.MEDIA, { action: 'sync', t: Date.now() });
      this.onReconnectedCb?.(); // → VoiceClient.reinitialize(): the SFU dropped our media, re-handshake.
    });

    this.room.onLeave(() => {
      if (this.intentionalLeave) return; // a deliberate logout / unmount — a clean exit, no alarm.
      // Anything reaching a terminal onLeave that we didn't initiate means the grace retries gave up
      // (or the server ended the seat). Either way, surface the calm "Connection lost — reload" path.
      this.setStatus('lost');
    });
  }

  /** Push a connection-health change to the subscriber (deduped) — drives the gentle HUD indicator. */
  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.onConnectionStatusCb?.(status);
  }

  /** Current connection health (M3.5b). Read by the dev `__duckNet` hook + the HUD. */
  connectionStatus(): ConnectionStatus {
    return this.status;
  }

  /** How many times we've transparently re-claimed our seat this session (drives the e2e + HUD). */
  reconnectAttempts(): number {
    return this.reconnectCount;
  }

  /**
   * DEV/e2e ONLY: force-close the underlying room websocket to SIMULATE a transient network blip —
   * WITHOUT a page reload and WITHOUT consenting — so the SDK's grace-reconnect path fires (`onDrop` →
   * retries → `onReconnect`). Exactly the drop a flaky network or a server bounce causes.
   *
   * Close code 4010 (`MAY_TRY_RECONNECT`) is deliberate: it's one of the four codes the SDK treats as
   * "abnormal, try to reconnect" (vs. 1000/CONSENTED which routes to a terminal onLeave), AND it sits in
   * the 3000–4999 range the browser WebSocket.close() actually permits (a browser throws on 1001/1005/
   * 1006). It's the same code the SDK itself uses to flag a recoverable transport drop. No-op in prod.
   */
  forceDropForTest(): void {
    const transport = (this.room.connection as unknown as { transport?: { ws?: WebSocket } }).transport;
    const ws = transport?.ws;
    try {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close(4010, 'duck-net dev drop'); // MAY_TRY_RECONNECT → SDK grace-reconnect kicks in
      } else {
        console.warn('[net] forceDropForTest: no open websocket to drop');
      }
    } catch (e) {
      console.warn('[net] forceDropForTest failed', e);
    }
  }

  sendMove(seq: number, dx: number, dy: number): void {
    this.room.send(ClientMessage.MOVE, { seq, dx, dy });
  }

  sendEmote(animation: string): void {
    this.room.send(ClientMessage.EMOTE, { animation });
  }

  /**
   * Broadcast my AUDIO-DEVICE state for the participant list's Discord-style 🎤/🎧 indicators.
   * `voiceOn` = my voice is live (I can hear the room); `micMuted` = my mic is deliberately muted (the
   * INTENT toggle, not the transient push-to-talk gate). The server stores it on my own PlayerState and
   * deltas it to everyone. Presence only — never logged or scored (Constitution Art. 1).
   */
  sendVoiceState(voiceOn: boolean, micMuted: boolean): void {
    this.room.send(ClientMessage.VOICE_STATE, { voiceOn, micMuted });
  }

  /**
   * Say a short line (M7a "Socialize") — a transient speech bubble that pops over my duck and fades.
   * We send only the text; the server trims, length-caps (`SAY_MAX_LEN`), rate-limits, and echoes a
   * SAID broadcast back to everyone (including me) so the bubble + accessible strip render uniformly.
   */
  sendSay(text: string): void {
    this.room.send(ClientMessage.SAY, { text });
  }

  /**
   * Send a quick emoji reaction (M7a) from the closed, wholesome `REACTIONS` set — it floats up from my
   * duck and fades. The server validates the reaction and broadcasts REACTED to everyone (including me).
   */
  sendReact(reaction: Reaction): void {
    this.room.send(ClientMessage.REACT, { reaction });
  }

  /**
   * Play a short, shared SOUND reaction (Task 6b) from the closed `SOUND_REACTIONS` catalog — the sonic
   * cousin of {@link sendReact}. We send only the catalog `sound` id; the server validates + rate-limits
   * (the emote limiter) and broadcasts SOUND_REACTED to everyone in the ROOM INCLUDING me (the server
   * `this.broadcast(...)`s room-wide — there is currently NO proximity gate), so the clip
   * plays uniformly off one path (no optimistic local echo). NOTE: per-listener BLOCK filtering is applied
   * CLIENT-SIDE on playback (a blocked sender is suppressed before audio/chatter — see OfficePage
   * `onSoundReacted`, FIX 1). Proximity-gating the broadcast is a possible future refinement.
   */
  sendSoundReact(sound: SoundReactionId): void {
    this.room.send(ClientMessage.SOUND_REACT, { sound });
  }

  /**
   * Send a MEDIA HUB control message (ADR-0033). One channel, discriminated by `action`; the server
   * derives the per-zone hub from our position. Playback control (play/pause/next/seek/playNow/remove/
   * reorder/clear/release/start+stopScreenshare) is only honoured for the zone HOST or guild OWNER — the
   * server re-checks and silently drops otherwise, so it is always safe to call. `enqueue`/`react`/`sync`/
   * `claimHost` are accepted from everyone. Fire-and-forget: the truth comes back on the schema delta
   * (read via {@link media}) / the MEDIA_CLOCK + MEDIA_REACTED messages — no optimistic local mutation.
   */
  sendMedia(msg: MediaControlMessage): void {
    this.room.send(ClientMessage.MEDIA, msg);
  }

  /**
   * Elect (or clear, with `'none'`) the presence flavor shown over my duck — a chosen flair, never
   * inferred from behaviour (M4). The server validates the flavor and syncs `presence` to everyone.
   */
  setPresence(flavor: PresenceFlavor): void {
    this.room.send(ClientMessage.SET_PRESENCE, { flavor });
  }

  /**
   * Broadcast my EQUIPPED cosmetics (M6 "Express Yourself") so everyone sees my look — the whole
   * equipped set, exactly like {@link setPresence} elects a single flair. The server validates each id
   * (a known cosmetic of its slot), stores it on my PlayerState, and syncs it to the room. Sent on join
   * (with the gateway-attested equipped set) and whenever I equip/unequip. Cosmetic-only + low-stakes:
   * the realtime trusts the client for DISPLAY; the valuable parts (coins, ownership) are gateway-attested.
   * Omitting a slot (or passing `undefined`) clears it for everyone — the look is always the full set.
   */
  sendCosmetics(equipped: EquippedCosmetics): void {
    this.room.send(ClientMessage.SET_COSMETICS, equipped);
  }

  /**
   * Use a nearby world object (a DELIBERATE action). We name only the object id; the server
   * re-checks proximity authoritatively, then applies the effect (elected presence + a one-shot
   * animation) and broadcasts it. The client never asserts proximity or the resulting state.
   */
  interact(objectId: string): void {
    this.room.send(ClientMessage.INTERACT, { objectId });
  }

  /**
   * Sit on / stand up from an anchored SEAT (Task 2 "interactive SIT") — a server-authoritative TOGGLE.
   * We name only the seat id; the server re-checks proximity + occupancy, then snaps + locks the duck on
   * the seat (or stands it up if it's the seat I'm already on). The client never asserts proximity or the
   * resulting position — the room owns positions + who is seated where.
   */
  sit(seatId: string): void {
    this.room.send(ClientMessage.SIT, { seatId });
  }

  /**
   * Do a beat of WORK while seated at a WORKSTATION desk (gamified-XP redesign — the small, repeatable
   * co-op office quest). No payload: the SERVER owns the authority — it re-checks that I'm actually
   * seated at a desk seat and enforces the WORK_COOLDOWN_MS cooldown, then attests the tiny +1 XP. A
   * request sent while standing (or inside the cooldown) is simply dropped server-side, so it's always
   * safe to call; the +1 QUEST_FIRED toast is the only feedback that a beat actually landed.
   */
  sendWork(): void {
    this.room.send(ClientMessage.WORK, {});
  }

  /**
   * Entryway front-door actions (ADR-0022). All three send an empty body — the SERVER owns the authority
   * (position + door state + host-ness), so it re-checks the rule and a request it can't honour is simply
   * dropped server-side. Always safe to call; the resulting sound / door-state change is the only feedback.
   *  - ringBell — ring the doorbell (the whole office hears the chime).
   *  - knock    — knock on the door (only nearby ducks hear it).
   *  - openDoor — open the door (host from anywhere, or anyone inside next to it).
   */
  ringBell(): void {
    this.room.send(ClientMessage.RING_BELL, {});
  }
  knock(): void {
    this.room.send(ClientMessage.KNOCK, {});
  }
  openDoor(open = true): void {
    this.room.send(ClientMessage.OPEN_DOOR, { open });
  }

  /**
   * Open or close an INTERIOR room door (Room Doors feature). Fire-and-forget: the server re-checks
   * {@link canToggleDoor} (proximity, or HOST from anywhere) and the truth comes back via the synced
   * `closedDoors` set — never an optimistic local mutation. Always safe to call; a request it can't honour
   * is simply dropped server-side.
   */
  toggleDoor(doorwayId: string, open: boolean): void {
    this.room.send(ClientMessage.TOGGLE_DOOR, { doorwayId, open });
  }

  /**
   * Buy & place a piece of FURNITURE (Furniture Store). Fire-and-forget: we send only the catalog `itemId`
   * + a snapped world position — NEVER a price. The server re-derives the price, checks the placer's coins
   * + the per-room cap, atomically charges + persists, then the piece arrives via the synced `furniture`
   * state (everyone sees it). The private reply (success + new balance, or "Not enough coins") comes back
   * via {@link onFurnitureResult}. Coins are charged ONLY on success — a cancelled placement sends nothing.
   */
  placeFurniture(itemId: string, x: number, y: number, rotation?: number): void {
    this.room.send(ClientMessage.PLACE_FURNITURE, {
      itemId,
      x,
      y,
      ...(rotation !== undefined ? { rotation } : {}),
    });
  }

  /** Remove a placed furniture piece by id (Furniture Store). The server re-checks the right to remove
   * (the placer, or the host); the truth comes back via the synced `furniture` set. */
  removeFurniture(furnitureId: string): void {
    this.room.send(ClientMessage.REMOVE_FURNITURE, { furnitureId });
  }

  /** Move (and optionally rotate) a placed piece — the "Take"/drop + "Rotate" context-menu actions. No
   * coins are charged (you own it). The server re-checks the right to move (placer/host) + the position;
   * the new transform comes back via the synced `furniture` set. */
  moveFurniture(furnitureId: string, x: number, y: number, rotation?: number): void {
    this.room.send(ClientMessage.MOVE_FURNITURE, {
      furnitureId,
      x,
      y,
      ...(rotation !== undefined ? { rotation } : {}),
    });
  }

  /**
   * Hold (or empty, with '') a fun prop in hand (Task 2 "HOLD ITEMS"). We send only the catalog id; the
   * server validates it against the closed `HELD_ITEMS` set ('' clears) and syncs `heldItem` on my
   * PlayerState so everyone sees the same prop. Cosmetic-only — like {@link setPresence}, it changes only
   * how my duck LOOKS, never what it can do.
   */
  setHeld(item: string): void {
    this.room.send(ClientMessage.SET_HELD, { item });
  }

  sendCelebrate(kind: CelebrationKind, targetUserId?: string, text?: string): void {
    this.room.send(ClientMessage.CELEBRATE, { kind, targetUserId, text });
  }

  /**
   * Ask the server to give reputation to a nearby duck. We send only the target's session id; the
   * server verifies proximity, applies the budget/cap, and replies via onReputationResult.
   */
  sendGiveReputation(targetSessionId: string): void {
    this.room.send(ClientMessage.GIVE_REPUTATION, { targetSessionId });
  }

  /**
   * Challenge a NEARBY duck to Rock-Paper-Scissors (M7b "Play Together"). We name only the target's
   * SESSION id; the server owns positions, re-checks proximity (SOCIAL.MINIGAME_RANGE), creates the
   * match, and invites the target. Playing — win OR lose — is the positive action; the point is the
   * moment of play together (never a ranking).
   */
  sendRpsChallenge(targetSessionId: string): void {
    this.room.send(ClientMessage.RPS_CHALLENGE, { targetSessionId });
  }

  /** Submit my choice (rock/paper/scissors) in an active RPS match (M7b). The server resolves both. */
  sendRpsChoose(matchId: string, choice: RpsChoice): void {
    this.room.send(ClientMessage.RPS_CHOOSE, { matchId, choice });
  }

  /** Decline / back out of an RPS match I was challenged to (M7b). A kind "no thanks", never a penalty. */
  sendRpsDecline(matchId: string): void {
    this.room.send(ClientMessage.RPS_DECLINE, { matchId });
  }

  /** Challenge a NEARBY duck to a Bark Battle (Challenge game #2). The server proximity-checks. */
  sendBarkChallenge(targetSessionId: string): void {
    this.room.send(ClientMessage.BARK_CHALLENGE, { targetSessionId });
  }

  /** Accept a Bark Battle I was invited to — the server stamps the shared countdown + window. */
  sendBarkAccept(matchId: string): void {
    this.room.send(ClientMessage.BARK_ACCEPT, { matchId });
  }

  /** Decline / back out of a Bark Battle. A kind "no thanks", never a penalty. */
  sendBarkDecline(matchId: string): void {
    this.room.send(ClientMessage.BARK_DECLINE, { matchId });
  }

  /** Stream one mic-loudness sample (slot `i`, points `v`) during an active battle window. */
  sendBarkSample(matchId: string, i: number, v: number): void {
    this.room.send(ClientMessage.BARK_SAMPLE, { matchId, i, v });
  }

  /**
   * Block a specific citizen's voice for me (M3.4 — per-listener self-protection). We send only the
   * target's SESSION id; the SERVER resolves it to a stable user and AUTHORITATIVELY severs the audio
   * link both ways (a blocked stream is closed server-side, never just muted locally). The block is
   * ephemeral (room memory only) and is never surfaced to or used against the blocked party
   * (Constitution Art. 1 — safety, not surveillance). Framing is self-protection: "block voice".
   */
  sendBlockCitizen(targetSessionId: string): void {
    this.room.send(ClientMessage.BLOCK, { targetSessionId });
  }

  /** Undo a voice block (M3.4): the server restores the audio link both ways. */
  sendUnblockCitizen(targetSessionId: string): void {
    this.room.send(ClientMessage.UNBLOCK, { targetSessionId });
  }

  /** Find the nearest *other* player to a world position, within `maxDist` units. */
  nearestOther(
    x: number,
    y: number,
    maxDist: number,
  ): { sessionId: string; player: SchemaPlayer } | null {
    let best: { sessionId: string; player: SchemaPlayer } | null = null;
    let bestDist = maxDist;
    for (const [sid, p] of this.players) {
      if (sid === this.selfSessionId) continue;
      const d = Math.hypot(p.x - x, p.y - y);
      if (d < bestDist) {
        bestDist = d;
        best = { sessionId: sid, player: p };
      }
    }
    return best;
  }

  self(): SchemaPlayer | undefined {
    return this.players.get(this.selfSessionId);
  }

  /**
   * Shared "Living World" ambient scalars (M4.2) read off the authoritative room state — the SAME
   * `timeOfDay`/`weather` the server delta's to everyone, so every duck shares one sky. Like
   * {@link players}, these are mutated in-place by the Colyseus SDK as patches arrive, so the render
   * loop reads them every frame (no React re-render). The room state is untyped (we join without a
   * schema generic), so we narrow defensively to `WorldAmbient` with safe daytime defaults — if the
   * server hasn't sent the scalars yet (or an older room), the world reads as a calm clear day.
   */
  ambient(): WorldAmbient {
    const s = this.room.state as { timeOfDay?: number; weather?: string } | undefined;
    const t = typeof s?.timeOfDay === 'number' ? s.timeOfDay : 0.35;
    const w = (s?.weather ?? 'clear') as Weather;
    return { timeOfDay: t, weather: w };
  }

  /**
   * The SHARED random world event everyone sees right now (M7b "Play Together") — read off the SAME
   * authoritative room state as {@link ambient}, so every duck sees the same spontaneous moment at the
   * same time (a pizza party or a fire drill), or 'none' when nothing's happening. Mutated in
   * place by the Colyseus SDK as patches arrive, so the render loop reads it every frame (no React
   * re-render). The room state is untyped (we join without a schema generic), so we narrow defensively
   * with a safe 'none' default — an older room (or before the first patch) reads as nothing happening.
   */
  worldEvent(): WorldEventKind {
    const s = this.room.state as { worldEvent?: string } | undefined;
    return (s?.worldEvent ?? 'none') as WorldEventKind;
  }

  /**
   * The guild's world THEME everyone shares right now (M8 "Guilds at Scale") — read off the SAME
   * authoritative room state as {@link ambient}/{@link worldEvent}, so every duck sees the same skin and
   * an owner's theme change re-skins the world live for everyone. Mutated in place by the Colyseus SDK as
   * patches arrive, so the render loop reads it every frame (no React re-render). The room state is
   * untyped (we join without a schema generic), so we narrow defensively with a safe 'office' default —
   * an older room (or before the first patch) reads as the original office. Cosmetic, never a capability.
   */
  theme(): WorldThemeId {
    const s = this.room.state as { theme?: string } | undefined;
    return (s?.theme ?? 'office') as WorldThemeId;
  }

  /**
   * The authoritative GEOMETRY LEVEL everyone in the room shares right now ('office' | 'medieval_castle') —
   * synced by the realtime server (Phase 2) off the resolved level, read off the SAME room state as
   * {@link theme}, mutated in place by the Colyseus SDK so the render loop reads it every frame (no React
   * re-render). The room state is untyped (we join without a schema generic), so we narrow defensively to a
   * safe 'office' default — an older room (or before the first patch) reads as the default office level. The
   * level is GEOMETRY (walls/zones/spawn); the cosmetic {@link theme} is independent.
   */
  levelId(): LevelId {
    const s = this.room.state as { levelId?: string } | undefined;
    const id = s?.levelId;
    return id === 'medieval_castle' || id === 'medieval_office' ? id : 'office';
  }

  /**
   * The entryway front-DOOR state (ADR-0022): true while the sealed vestibule's door is open. Read off
   * the SAME authoritative room state as {@link theme}, mutated in place by the Colyseus SDK, so the
   * render loop reads it every frame (no React re-render) to draw the door AND to feed
   * `officeWalls(entryDoorOpen)` into the client's collision prediction — matching the server's authority.
   * The room state is untyped (we join without a schema generic), so we narrow defensively to a safe
   * CLOSED default (an older room / pre-first-patch reads as a shut door — nobody phases through).
   */
  entryDoorOpen(): boolean {
    const s = this.room.state as { entryDoorOpen?: boolean } | undefined;
    return s?.entryDoorOpen === true;
  }

  /**
   * The ids of the interior room doors currently CLOSED (Room Doors feature). Read off the SAME
   * authoritative room state, mutated in place by the Colyseus SDK; the render loop reads it every frame
   * (no React re-render) to draw the door leaves AND to fold the closed-door slabs into the client's
   * collision prediction (via `composeWalls`) — matching the server's authority. Doors default OPEN, so an
   * absent id = open; the set is empty until someone shuts a door.
   */
  closedDoorIds(): string[] {
    const s = this.room.state as { closedDoors?: { forEach?: (cb: (v: unknown, k: string) => void) => void } } | undefined;
    const m = s?.closedDoors;
    const ids: string[] = [];
    if (m && typeof m.forEach === 'function') m.forEach((_v, k) => ids.push(k));
    return ids;
  }

  /**
   * WHO holds each closed interior door shut (door HOLDS, ADR-0060): doorway id → the holder's stable
   * userId ('' = unheld soft close — the holder left). Same authoritative map as {@link closedDoorIds}
   * (its VALUES); the render loop mirrors the server's `mayOpenHeldDoor` off this to label the prompt
   * "Open door" vs "Knock (locked)" — the server remains the only authority on an actual open.
   */
  doorHolds(): ReadonlyMap<string, string> {
    const s = this.room.state as { closedDoors?: { forEach?: (cb: (v: unknown, k: string) => void) => void } } | undefined;
    const m = s?.closedDoors;
    const holds = new Map<string, string>();
    if (m && typeof m.forEach === 'function') m.forEach((v, k) => holds.set(k, typeof v === 'string' ? v : ''));
    return holds;
  }

  /**
   * The zone ids whose DISCO is currently ON (ADR-0031 "Jukebox") — read off the SAME authoritative room
   * state as {@link closedDoorIds}, mutated in place by the Colyseus SDK as patches arrive, so the render
   * loop reads it every frame (no React re-render) to light only those rooms' floors. The room state is
   * untyped (we join without a schema generic), so we drain the MapSchema defensively via forEach into a
   * plain string[]; absent / empty = no disco (an older room reads as a dark floor). Player-driven +
   * room-local — the floor only lights where someone pressed a jukebox.
   */
  discoZoneIds(): string[] {
    const s = this.room.state as { discoZones?: { forEach?: (cb: (v: unknown, k: string) => void) => void } } | undefined;
    const m = s?.discoZones;
    const ids: string[] = [];
    if (m && typeof m.forEach === 'function') m.forEach((_v, k) => ids.push(k));
    return ids;
  }

  /**
   * Every PLACED FURNITURE piece in the room (Furniture Store) — read off the SAME authoritative room
   * state as {@link discoZoneIds}, a MapSchema mutated in place by the Colyseus SDK as patches arrive. The
   * render loop reads it every frame (no React re-render) and reconciles the furniture layer on a real
   * change. The state is untyped (we join without a schema generic), so we drain each piece defensively
   * into a plain {@link PlacedFurnitureView}. SHARED + room-level — everyone sees the same set.
   */
  furniture(): PlacedFurnitureView[] {
    const s = this.room.state as
      | { furniture?: { forEach?: (cb: (v: Record<string, unknown>, k: string) => void) => void } }
      | undefined;
    const m = s?.furniture;
    const out: PlacedFurnitureView[] = [];
    if (m && typeof m.forEach === 'function') {
      m.forEach((v, id) => {
        // w/h/rotation/scale of 0 mean "use the builder default"; only forward meaningful transforms so a
        // rug/long-desk falls back to its native size and an unrotated piece stays unrotated.
        const piece: PlacedFurnitureView = {
          id,
          kind: String(v.kind ?? ''),
          x: Number(v.x ?? 0),
          y: Number(v.y ?? 0),
          placedByUserId: String(v.placedByUserId ?? ''),
        };
        if (Number(v.rotation)) piece.rotation = Number(v.rotation);
        if (Number(v.scale) && Number(v.scale) !== 1) piece.scale = Number(v.scale);
        if (Number(v.w)) piece.w = Number(v.w);
        if (Number(v.h)) piece.h = Number(v.h);
        out.push(piece);
      });
    }
    return out;
  }

  /**
   * Live MEETING-ROOM quest progress (gamified-XP redesign), read off the SAME authoritative room state
   * as {@link ambient}/{@link theme}, so every client's HUD shows the same "meeting forming / in session"
   * bar without any per-user message. Mutated in place by the Colyseus SDK as patches arrive, so the
   * HUD's gentle poll reads it without a React re-render storm. The room state is untyped (we join without
   * a schema generic), so we narrow defensively to a calm idle default — an older room (or before the
   * first patch) reads as "nothing forming". Purely a celebratory cue, never a metric.
   */
  meeting(): OfficeQuestProgressView {
    const s = this.room.state as { meeting?: Partial<OfficeQuestProgressView> } | undefined;
    const m = s?.meeting;
    return {
      seated: typeof m?.seated === 'number' ? m.seated : 0,
      phase: m?.phase === 'standup' || m?.phase === 'meeting' ? m.phase : 'idle',
      elapsedSec: typeof m?.elapsedSec === 'number' ? m.elapsedSec : 0,
      targetSec: typeof m?.targetSec === 'number' ? m.targetSec : 0,
    };
  }

  /**
   * The MEDIA HUB for a given ZONE (ADR-0033) — read off the SAME authoritative room state as
   * {@link meeting}/{@link theme}. Each client reads the hub for the zone it stands in, so its own
   * YouTube IFrame / screen plays the same item at the same point. Mutated in place by the Colyseus SDK
   * as patches arrive, so the reconciler reads it without a React re-render storm (position is
   * extrapolated locally from `positionMs` + `updatedAt`). The room state is untyped (we join without a
   * schema generic), so we narrow defensively. Returns null when no hub exists for the zone yet (idle).
   */
  media(zoneId: string): MediaHubStateView | null {
    const hubs = (this.room.state as { mediaHubs?: { get?: (k: string) => unknown } } | undefined)
      ?.mediaHubs;
    const h = hubs && typeof hubs.get === 'function' ? hubs.get(zoneId) : undefined;
    if (!h || typeof h !== 'object') return null;
    return mapMediaHub(h as Record<string, unknown>, zoneId);
  }

  /** Estimated server-clock offset for the media hub (ms): `serverDateNow ≈ Date.now() + this` (ADR-0033). */
  mediaClockOffset(): number {
    return this.mediaClockOffsetMs;
  }

  /**
   * Count the screen shares currently live ACROSS ALL zones in this office (ADR-0034) — the client
   * pre-gate for the per-room concurrent-share cap, so the host gets an instant "limit reached" instead
   * of opening the OS picker only for the server to drop the start. The server re-checks authoritatively.
   */
  mediaScreenshareCount(): number {
    const hubs = (this.room.state as { mediaHubs?: { forEach?: (cb: (v: unknown) => void) => void } }
      | undefined)?.mediaHubs;
    if (!hubs || typeof hubs.forEach !== 'function') return 0;
    let n = 0;
    hubs.forEach((h) => {
      const now = (h as { now?: { id?: unknown; kind?: unknown } })?.now;
      if (now && typeof now.id === 'string' && now.id && now.kind === 'screenshare') n++;
    });
    return n;
  }

  // ── Voice signaling API (ADR-0009) ──────────────────────────────────────────────────────────────

  /**
   * Send a voice RPC over the room socket and resolve with its typed `data` payload when the matching
   * `voice:result` arrives. Generates a unique `id`, rejects on `ok:false`, on a timeout, or if the
   * room closes first — so the {@link VoiceClient} handshake can `await` each step linearly.
   *
   * The generic `T` is the expected `data` shape (e.g. `TransportParams`, `ConsumeResult`); the wire
   * envelope is untyped, so we trust the server contract here (the result schema is frozen).
   */
  sendVoiceRpc<T = unknown>(req: VoiceRpcRequestInput): Promise<T> {
    const id = `v${++this.voiceRpcSeq}-${Date.now().toString(36)}`;
    const message = { ...req, id } as VoiceRpcRequest;
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => {
        this.pendingVoiceRpc.delete(id);
        reject(new Error(`voice RPC '${req.method}' timed out`));
      }, VOICE_RPC_TIMEOUT_MS);
      this.pendingVoiceRpc.set(id, {
        resolve: (data) => resolve(data as T),
        reject,
        timer,
      });
      try {
        this.room.send(VoiceClientMessage.RPC, message);
      } catch (e) {
        // Send failed synchronously (socket already gone) — settle now, don't leak the entry.
        clearTimeout(timer);
        this.pendingVoiceRpc.delete(id);
        reject(e instanceof Error ? e : new Error('failed to send voice RPC'));
      }
    });
  }

  /** Resolve/reject the in-flight RPC whose id matches an incoming `voice:result`. */
  private settleVoiceRpc(result: VoiceRpcResult): void {
    const pending = this.pendingVoiceRpc.get(result.id);
    if (!pending) return; // unknown/duplicate/timed-out id — ignore
    clearTimeout(pending.timer);
    this.pendingVoiceRpc.delete(result.id);
    if (result.ok) pending.resolve(result.data);
    else pending.reject(new Error(result.error ?? 'voice RPC failed'));
  }

  /** Register the voice client's handler for "a peer just became audible" (server-authoritative). */
  onVoiceNewProducer(cb: ((notice: NewProducerNotice) => void) | null): void {
    this.onNewProducerCb = cb;
  }

  /** Register the voice client's handler for "a producer you were consuming is gone". */
  onVoiceProducerClosed(cb: ((notice: ProducerClosedNotice) => void) | null): void {
    this.onProducerClosedCb = cb;
  }

  /** Register the voice client's handler for the active-speaker set (drives speaking indicators). */
  onVoiceActiveSpeakers(cb: ((notice: ActiveSpeakersNotice) => void) | null): void {
    this.onActiveSpeakersCb = cb;
  }

  async leave(): Promise<void> {
    // Mark this as a DELIBERATE exit so the SDK's terminal onLeave (and any in-flight grace retries) is
    // treated as a clean leave, never as a `lost` connection (M3.5b). Also stop the SDK from retrying.
    this.intentionalLeave = true;
    this.room.reconnection.enabled = false;
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    // Fail any in-flight voice RPCs so awaiting callers don't hang past disconnect.
    for (const [, pending] of this.pendingVoiceRpc) {
      clearTimeout(pending.timer);
      pending.reject(new Error('room left'));
    }
    this.pendingVoiceRpc.clear();
    this.onNewProducerCb = null;
    this.onProducerClosedCb = null;
    this.onActiveSpeakersCb = null;
    try {
      // Race the consented leave against a short timeout: if we're leaving WHILE the socket is down
      // mid-grace (logout during a blip), the consented frame would buffer and room.leave()'s promise
      // (resolved on onLeave) could never settle — so cap the wait so logout/unmount never hangs.
      await Promise.race([
        this.room.leave(true),
        new Promise<void>((resolve) => setTimeout(resolve, 1500)),
      ]);
    } catch {
      /* already disconnected */
    }
  }
}
